<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useEquipmentsStore } from '@/store/equipments';
import { useCurrency } from '@/composables/useCurrency';
import { useLocation } from '@/composables/useLocation';
import { resolveImageUrl } from '@/composables/useImageUrl';
import SkeletonCard from './SkeletonCard.vue';

// Router and store
const router = useRouter();
const { formatPrice } = useCurrency();
const { getDistance } = useLocation();
const getFullImageUrl = resolveImageUrl;

const store = useEquipmentsStore();

// Local state
const isLoading = ref(false); 

// Computed properties from store
const equipments = computed(() => store.filteredEquipments);
const totalPages = computed(() => store.totalPages);
const pageLinks = computed(() => store.pageLinks);
const previousPageUrl = computed(() => store.previousPageUrl);
const nextPageUrl = computed(() => store.nextPageUrl);
const currentPage = computed(() => store.currentPage);

// 3 page numbers centred on current page
const visiblePages = computed(() => {
  const total = totalPages.value;
  const cur = currentPage.value;
  const pages = [];
  for (let p = cur - 1; p <= cur + 1; p++) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  return pages;
});

// Navigation functions (delegated to store)
const fetchPage = async (pageUrl) => {
  if (pageUrl) {
    isLoading.value = true;
    try {
      await store.fetchPage(pageUrl);
    } finally {
      isLoading.value = false;
    }
  }
};

const fetchNextPage = async () => {
  isLoading.value = true;
  try {
    await store.fetchNextPage();
  } finally {
    isLoading.value = false;
  }
};

const fetchPreviousPage = async () => {
  isLoading.value = true;
  try {
    await store.fetchPreviousPage();
  } finally {
    isLoading.value = false;
  }
};

// Navigation to detail page
const goToDetail = (equipmentId) => {
  if (equipmentId) {
    router.push({
      name: 'equipment-details',
      params: {
        id: encodeURIComponent(equipmentId),
      },
    });
  }
};

// Utility to render star ratings
const renderStars = (rating) => {
  const r = rating || 0;
  const full = Math.floor(r);
  const half = r % 1 >= 0.5 ? 1 : 0;
  return '★'.repeat(full) + (half ? '☆' : '') + '☆'.repeat(5 - full - half);
};

// Rate tier helpers per card
const activeRates = ref({}); // { [equipmentId]: 'day' | 'week' | 'month' }
const getActiveRate = (id) => activeRates.value[id] || 'day';
const setActiveRate = (id, tier) => { activeRates.value[id] = tier; };
const getCurrentRate = (eq) => {
  const tier = getActiveRate(eq.id);
  if (tier === 'week') return eq.weekly_rate ?? eq.hourly_rate * 7 * 0.9;
  if (tier === 'month') return eq.monthly_rate ?? eq.hourly_rate * 30 * 0.8;
  return eq.hourly_rate;
};
const getRatePeriod = (id) => {
  const t = getActiveRate(id);
  return t === 'week' ? 'Week' : t === 'month' ? 'Month' : 'Day';
};

// Owner helpers
const getOwnerName = (eq) => {
  const o = eq.owner;
  if (!o) return 'Unknown';
  if (o.first_name || o.last_name) return `${o.first_name || ''} ${o.last_name || ''}`.trim();
  if (o.company_name) return o.company_name;
  return 'Unknown';
};
const getOwnerInitial = (eq) => getOwnerName(eq).charAt(0).toUpperCase();
</script>



<template>
  <div class="container mx-auto px-1.5 py-4 sm:px-4">
    <!-- Skeleton Loading State: show until first equipment fetch completes -->
    <div
      v-if="!store.hasCompletedEquipmentFetch || store.isLoadingEquipments || isLoading"
      class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 sm:gap-4"
      role="status"
      aria-live="polite"
      aria-label="Loading items"
    >
      <SkeletonCard v-for="i in 10" :key="`skeleton-${i}`" />
    </div>

    <!-- (page size selector removed) -->

    <!-- Equipment Grid (only after first fetch confirmed) -->
    <div v-else>
      <div
        v-if="equipments.length > 0"
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 sm:gap-4"
        role="list"
        aria-label="List of item"
      >
        <div
          v-for="equipment in equipments"
          :key="equipment.id"
          class="bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
          role="listitem"
          :aria-label="`Equipment: ${equipment.name}`"
          @click="goToDetail(equipment.id)"
        >
          <!-- Badges -->
          <div v-if="equipment.is_featured || equipment.is_trending" class="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1">
            <span v-if="equipment.is_trending" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-primary-600 text-white shadow">
              <i class="pi pi-bolt text-[9px]"></i> Trending
            </span>
            <span v-if="equipment.is_featured" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-neutral-900 text-secondary-500 border border-secondary-500 shadow">
              <i class="pi pi-star-fill text-[9px]"></i> Featured
            </span>
          </div>

          <!-- Image -->
          <div class="relative aspect-[4/3] overflow-hidden bg-theme-bg2">
            <img
              v-if="equipment.images?.length > 0"
              :src="getFullImageUrl(equipment.images[0].image_url)"
              :alt="equipment.name"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            <div v-else class="absolute inset-0 flex items-center justify-center">
              <i class="pi pi-image text-4xl text-theme-muted"></i>
            </div>
            <span v-if="!equipment.is_available || equipment.available_quantity === 0" class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white">
              Unavailable
            </span>
          </div>

          <!-- Content -->
          <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
            <!-- Owner row -->
            <div class="flex items-center gap-1 sm:gap-1.5">
              <img v-if="equipment.owner?.image_url" :src="equipment.owner.image_url" :alt="getOwnerName(equipment)" class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border" />
              <div v-else class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600">{{ getOwnerInitial(equipment) }}</div>
              <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ getOwnerName(equipment) }}</span>
              <i v-if="equipment.owner?.is_verified" class="pi pi-verified text-sm text-primary-500" title="Verified"></i>
            </div>

            <!-- Item name -->
            <h3 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug" :title="equipment.name">
              {{ store.truncateText(equipment.name, 22) }}
            </h3>

            <!-- Rating + Distance row -->
            <div class="flex items-center justify-between text-[11px] sm:text-sm">
              <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
                <span class="text-yellow-500 hidden sm:inline">{{ renderStars(equipment.rating) }}</span>
                <span class="text-yellow-500 sm:hidden">★</span>
                <span class="text-theme-text2 truncate">{{ (equipment.rating || 0).toFixed(1) }} ({{ equipment.equipment_reviews?.length || 0 }})</span>
              </div>
              <span v-if="getDistance(equipment)" class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1">
                <i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>
                {{ getDistance(equipment) }}
              </span>
            </div>

            <!-- Pricing tiers -->
            <div class="mt-auto">
              <div class="flex items-center gap-1 mb-1">
                <button v-for="tier in ['day','week','month']" :key="tier"
                  @click.stop="setActiveRate(equipment.id, tier)"
                  class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors capitalize"
                  :class="getActiveRate(equipment.id) === tier ? 'bg-primary-500 text-white' : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'"
                >{{ tier === 'day' ? 'Day' : tier === 'week' ? 'Week' : 'Month' }}</button>
              </div>
              <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">
                {{ formatPrice(getCurrentRate(equipment)) }}
                <span class="text-theme-muted text-[10px] sm:text-xs font-normal">/ {{ getRatePeriod(equipment.id) }}</span>
              </p>
            </div>

            <!-- CTA -->
            <button
              @click.stop="goToDetail(equipment.id)"
              class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1"
              :class="equipment.is_featured ? 'bg-neutral-900 text-secondary-500 hover:bg-secondary-500 hover:text-neutral-900 focus:ring-secondary-500' : 'bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600'"
            >
              {{ equipment.is_featured ? '✨ Lease Now' : 'View Details' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="equipments.length === 0"
        class="text-center py-16 bg-theme-bg2 rounded-lg"
        aria-live="polite"
      >
        <i class="pi pi-inbox text-6xl text-theme-muted"></i>
        <h3 class="text-xl text-theme-text2 mt-4">No items available</h3>
        <p class="text-theme-muted mt-2">Please check back later or try different filters</p>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="totalPages > 1 && !isLoading && equipments.length > 0"
      class="flex items-center justify-center gap-2 mt-8"
      aria-label="Pagination"
    >
      <!-- Previous -->
      <button
        :disabled="!previousPageUrl"
        @click="fetchPreviousPage"
        class="pg-arrow"
        :class="!previousPageUrl ? 'pg-disabled' : 'pg-active'"
        aria-label="Previous page"
      >
        <i class="pi pi-chevron-left"></i>
        <span class="hidden sm:inline ml-1 text-xs font-medium">Prev</span>
      </button>

      <!-- 3 page numbers -->
      <button
        v-for="p in visiblePages"
        :key="p"
        @click="fetchPage(pageLinks.find(l => l.page === p)?.url)"
        class="pg-num"
        :class="p === currentPage ? 'pg-current' : 'pg-inactive'"
        :aria-current="p === currentPage ? 'page' : undefined"
      >
        {{ p }}
      </button>

      <!-- Next -->
      <button
        :disabled="!nextPageUrl"
        @click="fetchNextPage"
        class="pg-arrow"
        :class="!nextPageUrl ? 'pg-disabled' : 'pg-active'"
        aria-label="Next page"
      >
        <span class="hidden sm:inline mr-1 text-xs font-medium">Next</span>
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* ── Pagination ──────────────────────────────────────────── */
.pg-arrow {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 0.8rem;
  border: 1px solid var(--color-border, #e0e0e0);
  background: var(--color-cardBg, #ffffff);
  color: var(--color-text, #1c1c1c);
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;
  cursor: pointer;
  gap: 4px;
}
.pg-arrow.pg-active:hover {
  background: var(--color-secondary-500, #ffc107);
  color: #fff;
  border-color: var(--color-secondary-500, #ffc107);
  transform: translateY(-1px);
}
.pg-arrow.pg-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.pg-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  border: 1px solid var(--color-border, #e0e0e0);
  background: var(--color-cardBg, #ffffff);
  color: var(--color-text, #1c1c1c);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;
}
.pg-num.pg-current {
  background: var(--color-secondary-500, #ffc107);
  border-color: var(--color-secondary-500, #ffc107);
  color: #fff;
  font-weight: 700;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.35);
}
.pg-num.pg-inactive:hover {
  background: var(--color-bg2, var(--color-bgSecondary, #f5f5f5));
  border-color: var(--color-secondary-500, #ffc107);
  color: var(--color-secondary-500, #ffc107);
}


/* Focus styles for accessibility */
button:focus,
select:focus {
  outline: 2px solid var(--color-primary-400, #ff9e00);
  outline-offset: 2px;
}

/* Transition for smoother hover effects */
[class*="hover:"] {
  transition: all 0.2s ease;
}

/* Fade-in animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

/* Animated dots bouncing */
@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.3;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
.animate-bounce {
  animation: bounce 1.2s infinite ease-in-out;
}

/* Delay animations for a cascading effect */
.animation-delay-150 {
  animation-delay: 0.15s;
}
.animation-delay-300 {
  animation-delay: 0.3s;
}
</style>